/************************************************************************************
 * [COMPONENT]: L-HEADER
 * Controls main header functionality including cart interactions
 *************************************************************************************/

class LTAHeader extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// General elements
		// Cart module elements
		this.cartLinkEls = this.querySelectorAll('[data-cart-link]')

		// Menu module elements
		this.dropdownMenuEls = this.querySelectorAll('[data-menu-has-dropdown]')

		// Initialize event listeners
		this.menuDropdown_initMenuEvents()
		this.handleStickyMode()

		// Listen for cart updates from Rebuy
		document.addEventListener('rebuy:cart.change', this.cart_updateCartCount.bind(this))
	}

	// Sticky mode
	handleStickyMode() {
		window.addEventListener(
			'scroll',
			l_debounce(() => {
				const stickyHeightPoint = this.parentElement.offsetHeight + 100
				this.parentElement.classList.toggle('is-sticky-mode', window.scrollY > stickyHeightPoint)
			}, 50)
		)
	}

	// Cart module methods

	cart_updateCartCount(event) {
		const cart = event.detail.cart.cart

		this.cartLinkEls.forEach((cartLinkEl) => {
			const cartCountEl = cartLinkEl.querySelector('[data-cart-count]')
			cartCountEl.innerHTML = cart.item_count
		})
	}

	// Menu (desktop) module methods

	menuDropdown_initMenuEvents() {
		this.dropdownMenuEls.forEach((menuLinkEl) => {
			const dropdownMenuId = menuLinkEl.getAttribute('aria-controls')
			const dropdownMenuEl = document.getElementById(dropdownMenuId)

			if (!dropdownMenuEl) return

			menuLinkEl.addEventListener('click', (event) => {
				// event.preventDefault()

				const isOpen = dropdownMenuEl.classList.contains('is-active')

				isOpen ? this.menuDropdown_close(dropdownMenuEl) : this.menuDropdown_open(dropdownMenuEl, menuLinkEl, event)
			})

			menuLinkEl.addEventListener(
				'mouseenter',
				l_debounce(() => {
					this.menuDropdown_open(dropdownMenuEl, menuLinkEl, false)
				}, 200)
			)
			menuLinkEl.addEventListener(
				'mouseleave',
				l_debounce(() => {
					this.menuDropdown_close(dropdownMenuEl)
				}, 200)
			)
		})
	}

	menuDropdown_close(dropdownMenuEl) {
		dropdownMenuEl.classList.remove('is-active')

		// Accessibility
		dropdownMenuEl.setAttribute('hidden', '')
		dropdownMenuEl.setAttribute('tabindex', '-1')
	}

	menuDropdown_open(dropdownMenuEl, menuLinkEl, trapFocus = true) {
		// Accessibility
		dropdownMenuEl.removeAttribute('hidden')
		dropdownMenuEl.removeAttribute('tabindex')

		// Set up class / animate
		l_debounce(() => {
			dropdownMenuEl.classList.add('is-active')
		}, 50)()

		// Trap Focus
		if (trapFocus) l_trapFocus(menuLinkEl)
	}

	// Menu (desktop) module methods
}

customElements.define('l-header', LTAHeader)

/************************************************************************************
 * [MODULE]: MOBILE MENU DRAWERS
 * Controls mobile menu drawers functionality
 *************************************************************************************/

class LTAMainMenuDrawer extends LTADrawer {
	// To keep the focus on the trigger element based on the race condition
	drawer_close() {
		super.drawer_close()
	}
}

customElements.define('l-main-menu-drawer', LTAMainMenuDrawer)

/************************************************************************************
 * [MODULE]: SEARCH / PREDICTIVE SEARCH MODULE
 *************************************************************************************/

class LTASearchDrawer extends LTADrawer {
	connectedCallback() {
		super.connectedCallback()

		// Elements
		this.searchInputEl = this.querySelector('[data-search-input]')
		this.searchButtonEl = this.querySelector('[data-search-button]')
		this.searchResultsEl = this.querySelector('[data-results]')

		// Data
		this.cachedResults = {}

		// Initialize event listeners
		const debouncedOnChange = l_debounce(() => {
			this.handleSearchInput()
		}, 300)

		this.searchInputEl.addEventListener('input', debouncedOnChange)
	}

	handleSearchInput(event) {
		const searchTerm = this.searchInputEl.value.replace(' ', '-').toLowerCase()

		if (!searchTerm || searchTerm == '') {
			this.accessibility_disableSearchResults()

			return
		}

		this.accessibility_enableSearchResults()

		// Setup loading state
		this.enableLoadingState()

		if (this.cachedResults[searchTerm]) {
			this.renderSearchResults(this.cachedResults[searchTerm])

			return
		}

		// Fetch predictive search results
		const searchURL = `${window.LTATheme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=a-ajax-header-predictive-search`

		fetch(searchURL)
			.then((response) => {
				return response.text()
			})
			.then((htmlText) => {
				const resultsMarkup = l_getSectionDOM(htmlText, '#shopify-section-a-ajax-header-predictive-search')

				// Cache results
				this.cachedResults[searchTerm] = resultsMarkup

				// Render results
				l_debounce(() => {
					this.renderSearchResults(resultsMarkup)
				}, 100)()
			})
			.catch((error) => {
				console.error(error)
			})
			.finally(() => {
				// Remove loading state
				this.disableLoadingState()
			})
	}

	renderSearchResults(resultsMarkup) {
		// Update new content
		this.searchResultsEl.innerHTML = resultsMarkup.innerHTML

		// Update trap focus
		l_trapFocus(this, this.searchInputEl, false)
	}

	enableLoadingState() {
		// this.loadingEl.classList.remove('l-hidden')
		// this.searchResultsEl.classList.add('is-loading')
		// this.searchBtnEl.classList.add('is-loading')
	}

	disableLoadingState() {}

	accessibility_enableSearchResults() {
		this.searchResultsEl.classList.remove('l-hidden')
		this.searchResultsEl.removeAttribute('hidden')
		this.searchResultsEl.removeAttribute('tabindex')
	}

	accessibility_disableSearchResults() {
		this.searchResultsEl.classList.add('l-hidden')
		this.searchResultsEl.setAttribute('hidden', '')
		this.searchResultsEl.setAttribute('tabindex', '-1')
	}
}

customElements.define('l-search-drawer', LTASearchDrawer)

/************************************************************************************

* [COMPONENT]: L-HEADER-SEARCH-DRAWER

*************************************************************************************/
class LTAHeaderSearchDrawer extends LTADrawer {
	constructor() {
		super()
	}

	connectedCallback() {
		super.connectedCallback()

		// Elements
		this.formEl = this.querySelector('form')
		this.suggestionsEl = this.querySelector('[data-suggestions]')
		this.searchInputEl = this.querySelector('[data-search-input]')
		this.searchBtnEl = this.querySelector('[data-search-btn]')
		this.searchResultsEl = this.querySelector('[data-search-results]')
		this.clearBtnEl = this.querySelector('[data-search-clear]')
		this.loadingEl = this.querySelector('[data-search-loading]')

		// Data
		this.cachedResults = {}
		this.abortController = new AbortController()

		// Events
		const debouncedOnChange = l_debounce((event) => {
			this.handleSearchTermChange(event)
		}, 300)

		this.formEl.addEventListener('submit', debouncedOnChange.bind(this))
		this.searchInputEl.addEventListener('input', debouncedOnChange.bind(this))
		this.clearBtnEl.addEventListener('click', this.handleClearSearch.bind(this))

		// Run functions
		this.handleSearchTermsClickEvent()
		this.handleOnSearchPage()
	}

	handleSearchTermChange() {
		// Abort previous request
		this.abortController?.abort()
		this.abortController = new AbortController()

		// Setup loading state
		this.enableLoadingState()

		// Setup query key
		const queryKey = this.searchInputEl.value.replace(' ', '-').toLowerCase()

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey])

			return
		}

		// Fetch predictive search results
		const searchURL = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(queryKey)}&section_id=a-ajax-header-predictive-search`

		fetch(searchURL, {
			signal: this.abortController.signal,
		})
			.then((response) => {
				return response.text()
			})
			.then((htmlText) => {
				const resultsMarkup = l_getSectionDOM(htmlText, '#shopify-section-a-ajax-header-predictive-search')

				// Cache results
				this.cachedResults[queryKey] = resultsMarkup

				// Render results
				this.renderSearchResults(resultsMarkup)
			})
			.catch((error) => {
				console.error(error)

				this.disableLoadingState()
			})
	}

	handleSearchTermsClickEvent() {
		this.suggestionTermEls = this.querySelectorAll('[data-term-suggestion]')

		this.suggestionTermEls.forEach((suggestionTermEl) => {
			suggestionTermEl.addEventListener('click', (event) => {
				event.preventDefault()

				const searchTerm = suggestionTermEl.dataset.termSuggestion

				this.searchInputEl.value = searchTerm
				this.searchInputEl.focus()

				this.formEl.dispatchEvent(new Event('submit'))
			})
		})
	}

	renderSearchResults(resultsMarkup) {
		this.searchResultsEl.innerHTML = resultsMarkup.innerHTML

		// Remove loading state
		this.disableLoadingState()
	}

	handleClearSearch() {
		this.searchInputEl.value = ''
		this.searchInputEl.focus()

		this.searchResultsEl.innerHTML = ''

		this.loadingEl.classList.add('l-hidden')
		this.searchResultsEl.classList.remove('is-loading')
		this.searchResultsEl.classList.add('is-no-search')
	}

	handleOnSearchPage() {
		const templateName = this.dataset.templateName

		const isSearchPageWithoutQuery = () => {
			const currentURL = window.location.pathname
			const isSearchPageWithoutQuery = currentURL == this.dataset.searchUrl && window.location.search == ''

			return isSearchPageWithoutQuery
		}

		// Check if the current page is the search page
		if (templateName == 'search') {
			if (isSearchPageWithoutQuery()) this.drawer_open()
		}

		// Setup popstate event listener
		window.addEventListener('popstate', () => {
			if (isSearchPageWithoutQuery()) this.drawer_open()
		})
	}

	enableLoadingState() {
		this.loadingEl.classList.remove('l-hidden')
		this.searchResultsEl.classList.add('is-loading')
		this.searchBtnEl.classList.add('is-loading')
	}

	disableLoadingState() {
		this.loadingEl.classList.add('l-hidden')
		this.searchResultsEl.classList.remove('is-loading')
		this.searchBtnEl.classList.remove('is-loading')
		this.searchResultsEl.classList.remove('is-no-search')
	}

	drawer_open() {
		super.drawer_open()

		this.searchInputEl.focus()
	}
}

customElements.define('l-header-search-drawer', LTAHeaderSearchDrawer)

